import axios from 'axios';

import { extractAI, parseAIError, type AIErrorInfo } from '@/utils/extractAI.ts';

export type TranscribeResult = {
  text: string;
  language: string;
  duration_seconds: number | null;
  provider: string;
  model: string;
  latency_ms: number;
};

export class TranscribeError extends Error {
  readonly info: AIErrorInfo;

  constructor(info: AIErrorInfo) {
    super(info.message);
    this.name = 'TranscribeError';
    this.info = info;
  }
}

type TranscribeAudioParams = {
  blob: Blob;
  /** File extension matching the container (webm, m4a, ogg…) — the vendor sniffs by it. */
  extension: string;
  userId?: string;
  vesselId?: string;
  /** Which chat the note comes from; recorded in the usage log only. */
  source: string;
  signal?: AbortSignal;
};

/**
 * POST /api/ai/speech/transcribe — a recorded voice note in, English text out.
 * Errors come back typed (`TranscribeError.info.code`: audio_too_long,
 * no_speech_detected, model_quota_exhausted…) so the caller can word them; the
 * generic AI toast is suppressed for the same reason.
 */
export const transcribeAudio = async ({
  blob,
  extension,
  userId,
  vesselId,
  source,
  signal,
}: TranscribeAudioParams): Promise<TranscribeResult> => {
  const formData = new FormData();
  formData.append('file', blob, `voice-note.${extension}`);
  if (userId) formData.append('user_id', userId);
  if (vesselId) formData.append('vessel_id', vesselId);
  formData.append('source', source);

  const response = await extractAI({
    formData,
    type: 'speech',
    method: 'transcribe',
    signal,
    returnErrorResponse: true,
    suppressErrorToast: true,
  });

  if (!response) {
    // Cancelled, or a network failure extractAI already swallowed.
    throw new TranscribeError({
      message: 'Couldn’t reach the transcription service. Please try again.',
      retryable: true,
    });
  }
  if (response.status >= 400) {
    throw new TranscribeError(
      parseAIError(
        new axios.AxiosError('transcribe failed', undefined, undefined, undefined, response),
      ),
    );
  }
  return response.data as TranscribeResult;
};

/** Drop the transcript into an input: after existing text, with a single separating space. */
export const appendTranscript = (current: string, transcript: string) => {
  const text = transcript.trim();
  if (!text) return current;
  if (!current.trim()) return text;
  return `${current.replace(/\s+$/, '')} ${text}`;
};
